import React, { useMemo, useState } from 'react';
import {
  HbrIcon,
  HbrSpinner,
  HbrTextarea,
} from '@harbor/elements-react-standalone';
import {
  useCreateApprovalHistoryMutation,
  useCreateApprovalRequestMutation,
  useFetchApprovalRequestByObjectIds,
  useUpdateApprovalRequestMutation
} from '../../hooks/approval-request-hooks';
import {
  ApprovalRequest,
  ApprovalRequestPayload,
  ApprovalRequestSource,
  ApprovalRequestStatusLevel,
  ApprovalRequestType,
  RequestHistoryPayload
} from '../../types/approval-request';
import {
  alertsInfoSmallBorder,
  alertsNegativeSmallBorder,
  alertsPositiveSmallBorder,
  alertsWarningSmallBorder
} from '@harbor/elements/icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../support/store';
import { useModal } from '../security-toolbox-ui/StbModalContext';
import StbButton from '../security-toolbox-ui/StbButton';
import { titleCase } from '../security-toolbox-ui/security-toolbox-ui-utils';
import StbApiModal from '../security-toolbox-ui/StbApiModal';
import ApprovalRequestModal from '../Approvals/ApprovalRequestModal';
import { useParams } from 'react-router-dom';
import { useFetchOpportunity } from '../../hooks/opportunity-hooks';
import { useFetchPov } from './opportunityDetailHelpers';
import { postItem } from '../../support/queries';


interface PoVRequestComponentProps {
  requestTitle: string;
  selectedProduct: number;
  requestActionsEnabled?: boolean;
  onRequestOpen?: () => Promise<void> | null;
}

const PoVApprovalRequestComponent: React.FC<PoVRequestComponentProps> = ({
  requestTitle,
  selectedProduct,
  requestActionsEnabled = false,
  onRequestOpen = null
}) => {
  const { id: opportunityId } = useParams();
  const { data: opportunity } = useFetchOpportunity(opportunityId);
  const { data: pov, isLoading: loadingPov } = useFetchPov(opportunity);

  const requestObjectIds = useMemo(() => ({
    [ApprovalRequestSource.PoV]: pov?.id,
    [ApprovalRequestSource.Opportunity]: opportunityId,
    [ApprovalRequestSource.OpportunityProduct]: selectedProduct
  }), [pov?.id, opportunityId, selectedProduct]);

  const { mutateAsync: createApprovalRequest, isPending: creating } = useCreateApprovalRequestMutation(requestObjectIds);
  const { mutateAsync: createApprovalHistory } = useCreateApprovalHistoryMutation();
  const { mutateAsync: updateApprovalRequest } = useUpdateApprovalRequestMutation(requestObjectIds);

  // REQUESTER
  const { name: requester, email: requesterEmail, id: requesterId } = useSelector((state: RootState) => state.user.userData);

  // APPROVER
  const { name: approver, email: approverEmail, id: approverId } = useSelector((state: RootState) => state.user.manager);
    
  const requestItemType = ApprovalRequestType.PoV;

  // EXISTING REQUEST
  const { data: existingRequest, isLoading: isLoadingExistingRequest } = useFetchApprovalRequestByObjectIds(requestItemType, requestObjectIds);

  // MODAL
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [newRequest, setNewRequest] = useState<boolean>(true);
  const [justification, setJustification] = useState('');

  const { openModal, closeModal } = useModal();
  
  const handleCancelRequestClick = () => {
    openModal({
      content:  () => (
        <StbApiModal
          open={true}
          role="delete"
          submitLabel="Cancel Request"
          submitSuccess="Request cancelled"
          onSubmit={handleCancelRequest}
          onClose={async () => closeModal()}
        >
          <div>Are you sure you want to cancel this request?</div>
        </StbApiModal>
      )
    });
  };

  const handleOpen = async () => {
    if (onRequestOpen) {
      await onRequestOpen();
    }
    setRequestModalOpen(true);
  };

  const handleCancelRequest = async () => {
    if (!existingRequest || !existingRequest?.id) return;

    const status = ApprovalRequestStatusLevel.Cancelled;
    const payload: Omit<ApprovalRequestPayload, 'requested_item_id'> = {
      id: existingRequest.id,
      requestor_id: existingRequest?.requestor.id,
      approver_id: existingRequest?.approver.id,
      requested_item_type: existingRequest?.requested_item_type,
      status_id: status,
    };
    await updateApprovalRequest(payload);

    const history: RequestHistoryPayload = {
      approval_request_id: existingRequest.id,
      previous_status_id: existingRequest.status.id ?? null,
      current_status_id: status,
      action_by_user_id: existingRequest.requestor.id
    };
    await createApprovalHistory(history);
  }

  const handleClose = async () => {
    setRequestModalOpen(false);
    setJustification('');
  };

  const handleRequest = async (justification?: string) => {
    const status = ApprovalRequestStatusLevel.Pending;
    let payload: Partial<ApprovalRequestPayload> = {
      requestor_id: existingRequest?.requestor.id ?? requesterId,
      approver_id: existingRequest?.approver.id ?? approverId,
      requested_item_type: existingRequest?.requested_item_type ?? requestItemType,
      status_id: status,
    };
    let result: Partial<ApprovalRequest>;

    if (newRequest) {
      const itemPayload: Record<string, any> = {
        pov_id: requestObjectIds[ApprovalRequestSource.PoV],
        opportunity_id: requestObjectIds[ApprovalRequestSource.Opportunity],
        opportunity_product_id: requestObjectIds[ApprovalRequestSource.OpportunityProduct]
      };
      const itemResult = await postItem('db/approval-request-item', itemPayload);

      if (itemResult?.id) {
        payload = {
          ...payload,
          title: requestTitle,
          requested_item_id: itemResult.id,
          justification
        };
        result = await createApprovalRequest(payload as ApprovalRequestPayload);
      }
    } else {
      payload = {
        ...payload,
        id: existingRequest?.id,
      };
      result = await updateApprovalRequest(payload as ApprovalRequest);
    }

    if (result?.id) {
      const history: RequestHistoryPayload = {
        previous_status: newRequest ? null : existingRequest?.status.id ?? null,
        current_status_id: status,
        previous_justification: newRequest ? null : existingRequest?.justification ?? null,
        current_justification: justification ?? null,
        action_by_user_id: existingRequest?.requestor.id ?? requesterId,
        approval_request_id: result.id
      };
      await createApprovalHistory(history);
    }

    setNewRequest(false);
  };

  const getStatusIcon = (statusId: ApprovalRequestStatusLevel) => {
    switch (statusId) {
      case ApprovalRequestStatusLevel.Pending:
      case ApprovalRequestStatusLevel.Cancelled:
        return <HbrIcon asset={alertsInfoSmallBorder} size="24px" className="mt-1 ml-1" sentiment="info" />;
      case ApprovalRequestStatusLevel.Approved:
        return <HbrIcon asset={alertsPositiveSmallBorder} size="24px" className="mt-1 ml-1" sentiment="success" />;
      case ApprovalRequestStatusLevel.Denied:
        return <HbrIcon asset={alertsNegativeSmallBorder} size="24px" className="mt-1 ml-1" sentiment="danger" />;
      case ApprovalRequestStatusLevel.UpdateRequired:
        return <HbrIcon asset={alertsWarningSmallBorder} size="24px" className="mt-1 ml-1" sentiment="warning" />;
      default:
        return null;
    }
  };

  if (!requestActionsEnabled) {
    return isLoadingExistingRequest
      ? (
          <div className={'border border-gray-300 rounded-md p-2 w-100 h-auto mb-4'}>
            <HbrSpinner size="small" />
          </div>
        )
      : existingRequest 
        ? (
            <div key={existingRequest?.id} className="border border-gray-300 rounded-md p-2 grid grid-cols-[24px_1fr] gap-x-4 items-center">
              <div>{getStatusIcon(existingRequest?.status?.id)}</div>
              <div className="text-sm text-gray-600">{titleCase(requestItemType)} approval request {existingRequest?.status?.title}</div>
            </div>
          )
        : null;
  }

  return (
    <>
      <div className={'border border-gray-300 rounded-md p-2 w-100 h-auto mb-4'}>
        {isLoadingExistingRequest 
          ? <HbrSpinner size="small" />   
          : existingRequest
            ? (
                <div key={existingRequest?.id} className={'grid grid-cols-[24px_1fr_1fr] gap-x-4 items-center'}>
                  <div>{getStatusIcon(existingRequest?.status?.id)}</div>
                  <div className='text-sm text-gray-600'>{titleCase(requestItemType)} approval request {existingRequest?.status?.title}</div>
                  <div className="justify-self-end">
                    {[ApprovalRequestStatusLevel.Denied, ApprovalRequestStatusLevel.Cancelled].includes(existingRequest?.status?.id) && (
                      <StbButton
                        pill={false}
                        role="request"
                        onClick={() => {
                          setNewRequest(true);
                          handleOpen();
                        }}
                      >
                        Send New Request
                      </StbButton>
                    )}
                    {[
                      ApprovalRequestStatusLevel.Pending,
                      ApprovalRequestStatusLevel.UpdateRequired
                    ].includes(existingRequest?.status?.id) && (
                      <StbButton
                        pill={false}
                        role="delete"
                        onClick={handleCancelRequestClick}
                      >
                        Cancel Request
                      </StbButton>
                    )}
                    {existingRequest?.status?.id === ApprovalRequestStatusLevel.UpdateRequired && (
                      <StbButton
                        pill={false}
                        role="request"
                        onClick={() => {
                          setNewRequest(false);
                          handleOpen();
                        }}
                        className="ml-2"
                      >
                        Resend Request
                      </StbButton>
                    )}
                  </div>
                </div>
              )
            : (
                <StbButton
                  pill={false}
                  role="request"
                  onClick={() => {
                    setNewRequest(true);
                    handleOpen();
                  }}
                >
                  Request {titleCase(requestItemType)} Approval
                </StbButton>
              )
          }
      </div>
      <ApprovalRequestModal
        open={requestModalOpen}
        requestItemType={requestItemType}
        requestTitle={requestTitle}
        requestObjectIds={requestObjectIds}
        handleClose={handleClose}
        handleRequest={handleRequest}
      >
        <HbrTextarea
          label="Justification (optional)"
          value={justification}
          onHbr-change={e => setJustification(e.target.value)}
          placeholder="Add extra details to support your request..."
          rows={3}
        />
      </ApprovalRequestModal>
    </>
  );
};

export default PoVApprovalRequestComponent;